import { createId } from '@paralleldrive/cuid2';

import { countries } from '../../utils/countries.mjs';
import { MIN_PASSWORD_LENGTH, MAX_PASSWORD_LENGTH } from '../../utils/const/passwords.mjs';

$ {
  let elementId;
  let fieldId;

  const {
    githubAuthHref,
    googleAuthHref,
    hasPasskeysSignup,
    hasPasskeysSupport,
    hasPasswordSignup,
    partner,
    partnerDisplayname,
    partnerLegalName,
    qualcommAuthHref,
    shellAuthHref,
    signupTemplateConfig,
    withInvite,
  } = input;
  const { isLoading, signupMethod, step, formData, isPasswordPwned, passwordState, passwordFieldVisibility } = state;
  const formId = createId();
}

<app-notification no-update key="notification" />

<div class:no-update="columns is-centered">
  <div class:no-update="column is-two-fifths">
    <app-box>
      <form key="form" id:scoped=formId method="post" autocomplete="on" on-submit("handleFormSubmit")>
        <div class=[step === 1 && "is-hidden"]>
          <p no-update class="help mt-0 mb-0">
            Fill in the required fields, then click <span class="has-text-weight-semibold">Continue</span> to select the signup method.
          </p>
          <p no-update class="help mt-0 mb-5">
            Fields marked with <span class="has-text-weight-semibold">*</span> are required.
          </p>
          <fieldset disabled=(isLoading && true) no-update-body-if(isLoading || step === 1) on-input("handleSignupFormInput")>
            <div no-update class="field signup-field" aria-hidden="true">
              $ elementId = createId();
              <input id:scoped=elementId type="text" name=`signup_${elementId}` tabindex="-1" value="">
            </div>

            <input id:scoped=createId() type="hidden" name="with_invite" value=`${withInvite ? 1 : 0}`>

            $ fieldId = createId();
            <div class="field">
              <label class="label required" for:scoped=fieldId>Full name</label>
              <div class="control">
                <input
                  required
                  aria-required="true"
                  class="input"
                  id:scoped=fieldId
                  type="text"
                  inputmode="text"
                  name="name"
                  autocomplete="name"
                  autofocus
                  value=(formData["name"] && formData["name"]) />
              </div>
            </div>

            $ fieldId = createId();
            <div class="field">
              <label class="label required" for:scoped=fieldId>Email address</label>
              <p class="help mb-2 mt-0">
                <if(partner && (partnerLegalName || partnerDisplayname))>
                  <span>${partnerLegalName ?? partnerDisplayname}</span> and Foundries.io Ltd. will use your email to authenticate you and to provide the service.
                </if>
                <else>
                  We will use your email to authenticate you and to send you occasional notifications.
                </else>
              </p>
              <div class="control">
                <input
                  required
                  aria-required="true"
                  class="input"
                  id:scoped=fieldId
                  type="email"
                  inputmode="email"
                  name="email"
                  autocomplete="username"
                  autofocus
                  value=(formData["email"] && formData["email"])/>
              </div>
            </div>

            $ fieldId = createId();
            <div class:no-update="field">
              <label no-update-body class:no-update="label required" for:scoped=fieldId>Company</label>
              <div class:no-update="control">
                <input
                  class="input"
                  id:scoped=fieldId
                  type="text"
                  inputmode="text"
                  name="company"
                  autocomplete="organization"
                  autofocus
                  required
                  aria-required="true"
                  value=(formData["company"] && formData["company"])/>
              </div>
            </div>

            <div class:no-update="field">
              $ fieldId = createId();
              <label no-update-body for:scoped=fieldId class="label required">Country</label>
              <div class:no-update="select is-fullwidth">
                <select key="country" id:scoped=fieldId name="country" autocomplete="country" required>
                  <option disabled selected value="">&mdash; Select one &mdash;</option>
                  <for|value, idx| of=countries>
                    <option no-update-body key=`country-${idx}` value=value[0]>
                      ${value[1]}
                    </option>
                  </for>
                </select>
              </div>
            </div>

            <if(signupTemplateConfig.hasFirstContactQuestion)>
              <div class:no-update="field">
                $ fieldId = createId();
                <label no-update-body for:scoped=fieldId class="label required">How did you first hear about us?</label>
                <div class:no-update="control">
                  <input
                    class="input"
                    id:scoped=fieldId
                    type="text"
                    inputmode="text"
                    name="first_contact"
                    required
                    aria-required="true"
                    minlength="3"
                    maxlength="255"
                    value=(formData["first_contact"] && formData["first_contact"])/>
                </div>
              </div>
            </if>

            <if(signupTemplateConfig.hasTermsConditions)>
              <div class:no-update="field">
                <label class:no-update="checkbox is-inline-flex is-align-items-center required">
                  <input required aria-required="true" type="checkbox" name="terms" value="1" checked=(formData["terms"] && true) />&nbsp;
                  I agree to the&nbsp;<a target="_blank" href=signupTemplateConfig.termsUrl>Terms of Service</a>&nbsp;and&nbsp;<a target="_blank" href=signupTemplateConfig.privacyPolicyUrl>Privacy Policy</a>
                </label>
              </div>
            </if>

            <if(signupTemplateConfig.hasTechnicalDemo)>
              <div class:no-update="field">
                <label class:no-update="checkbox is-inline-flex is-align-items-center">
                  <input type="checkbox" name="demo" value="1" checked=(formData["demo"] && true) />&nbsp;
                  I am interested in a technical demo
                </label>
              </div>
            </if>

            <if(signupTemplateConfig.hasNewsletterSubscribe)>
              <div class:no-update="field">
                <label class:no-update="checkbox is-inline-flex is-align-items-center">
                  <input type="checkbox" name="newsletter" value="1" checked=(formData["newsletter"] && true) />&nbsp;
                  I agree to receive product news via email
                </label>
                <p no-update class="help">
                  You can opt-out from these emails in your account page anytime.
                </p>
              </div>
            </if>

            <div class:no-update="field mt-5">
              <button no-update-body class:no-update="button is-dark is-fullwidth" data-action="next" on-click("handleSignupNextBackClick")>
                <span class="icon-text is-align-items-center is-pointer-events-none">
                  <span>
                    Continue
                  </span>
                  <span class="icon is-small">
                    <i class="fa-regular fa-arrow-right-long"></i>
                  </span>
                </span>
              </button>
            </div>
          </fieldset>
        </div>

        <div class=[step === 0 && "is-hidden"]>
          <div class:no-update="control mb-5">
            <button no-update-body class:no-update="button is-small" data-action="back" on-click("handleSignupNextBackClick")>
              <span class="icon-text is-align-items-baseline is-pointer-events-none">
                <span class="icon is-small">
                  <i class="fas fa-regular fa-arrow-left-long"></i>
                </span>
                <span>
                  Go back
                </span>
              </span>
            </button>
          </div>

          <p no-update class="help mt-0 mb-5">
            Select the signup method to use, provide the required information if any, then click on the signup button.
          </p>

          <div class:no-update="field is-flex is-flex-direction-column has-row-gap-5" on-click("handleSignupMethodClick")>

            <if(hasPasskeysSignup && hasPasskeysSupport)>
              <div class:no-update="control is-flex">
                <label class:no-update="radio is-flex is-inline-flex is-align-items-baseline is-flex-grow-1 has-column-gap-1">
                  <input type="radio" name="signup-method" data-signup-method="passkey" required=(step === 1 && true) checked=(signupMethod === 'passkey' && true) value="passkey" />
                  <div class:no-update="is-flex is-flex-direction-column is-flex-grow-1">
                    <p no-update class="mb-1">Use a passkey</p>
                    <a no-update class="is-size-7 mb-2" target="_blank" href="https://passkeys.dev/docs/intro/what-are-passkeys/">What are passkeys?</a>
                    <fieldset disabled=(signupMethod !== "passkey" && true) data-fieldset-passkey>
                      <button no-update-body name="signup-pk" class=["button is-dark is-fullwidth", isLoading && "is-loading"] on-click("handlePasskeyClick")>
                        <span class="icon-text is-pointer-events-none">
                          <span class="icon" aria-hidden="true">
                            <i class="fa-light fa-fingerprint"></i>
                          </span>
                          <span>
                            Sign up with Passkey
                          </span>
                        </span>
                      </button>
                    </fieldset>
                  </div>
                </label>
              </div>
            </if>

            <if(githubAuthHref || googleAuthHref || qualcommAuthHref || shellAuthHref)>
              <div class:no-update="control is-flex">
                <label class:no-update="radio is-flex is-inline-flex is-flex-grow-1 is-align-items-baseline has-column-gap-1">
                  <input type="radio" name="signup-method" data-signup-method="external" required=(step === 1 && true) checked=(signupMethod === 'external' && true) value="external" />
                  <div class:no-update="is-flex is-flex-direction-column is-flex-grow-1 has-row-gap-2">
                    <p no-update>Use an external provider</p>
                    <fieldset disabled=(signupMethod !== "external" && true) data-fieldset-external>
                      <div class:no-update="is-flex is-flex-direction-column is-flex-grow-1 has-row-gap-2">
                        <if(githubAuthHref)>
                          <a no-update-body aria-label="Sign up with GitHub" data-signup="github" href=githubAuthHref rel="nofollow" role="button" class=["button is-dark", isLoading && "is-loading"] data-no-instant on-click("onSocialLoginClick", "github")>
                            <span class="icont-text is-pointer-events-none">
                              <span aria-hidden="true" class="icon">
                                <i class="fa-brands fa-github"></i>
                              </span>
                              <span>Sign up with GitHub</span>
                            </span>
                          </a>
                        </if>

                        <if(googleAuthHref)>
                          <a no-update-body aria-label="Sign up with Google" data-signup="google" href=googleAuthHref rel="nofollow" role="button" class=["button is-dark", isLoading && "is-loading"] data-no-instant on-click("onSocialLoginClick", "google")>
                            <span class="icon-text is-pointer-events-none">
                              <span aria-hidden="true" class="icon">
                                <i class="fa-brands fa-google"></i>
                              </span>
                              <span>Sign up with Google</span>
                            </span>
                          </a>
                        </if>

                        <if(qualcommAuthHref)>
                          <a no-update-body aria-label="Sign up with Qualcomm ID" data-signup="qualcomm" href=qualcommAuthHref rel="nofollow" role="button" class=["button is-dark", isLoading && "is-loading"] data-no-instant on-click("onSocialLoginClick", "qualcomm")>
                            <span class="icon-text is-pointer-events-none">
                              <span aria-hidden="true" class="icon">
                                <i class="fa-kit fa-brand-qualcomm"></i>
                              </span>
                              <span>Sign up with Qualcomm ID</span>
                            </span>
                          </a>
                        </if>

                        <if(shellAuthHref)>
                          <a no-update-body aria-label="Sign up with Shell ID" data-signup="qualcomm" href=shellAuthHref rel="nofollow" role="button" class=["button is-dark", isLoading && "is-loading"] data-no-instant on-click("onSocialLoginClick", "shell")>
                            <span class="icon-text is-pointer-events-none">
                              <span aria-hidden="true" class="icon">
                                <i class="fa-kit fa-brand-shell"></i>
                              </span>
                              <span>Sign up with Shell ID</span>
                            </span>
                          </a>
                        </if>

                      </div>
                    </fieldset>
                  </div>
                </label>
              </div>
            </if>

            <if(hasPasswordSignup)>
              <div class:no-update="control is-flex">
                <label class:no-update="radio is-flex is-inline-flex is-align-items-baseline is-flex-grow-1 has-column-gap-1">
                  <input type="radio" name="signup-method" data-signup-method="local" required=(step === 1 && true) checked=(signupMethod === "local" && true) value="local" />
                  <div class:no-update="is-flex is-flex-direction-column has-row-gap-2 is-flex-grow-1">
                    <p no-update>Use email and password</p>
                    <fieldset disabled=(signupMethod !== "local" && true) data-fieldset-password on-input("handlePasswordFieldsetInput") on-click("handlePasswordFieldsetClicks")>
                      $ const isRequired = (step === 1 && signupMethod === "local");
                      <div class:no-update="is-flex is-flex-direction-column is-flex-grow-1 has-row-gap-2">
                        $ fieldId = createId();

                        <div class:no-update="field">
                          <if(isRequired)>
                            <label class="label mb-1 required" for:scoped=fieldId>Password</label>
                          </if>
                          <else>
                            <label class="label mb-1" for:scoped=fieldId>Password</label>
                          </else>
                          <if(isPasswordPwned)>
                            <p no-update class="help is-danger mb-2 mt-0">
                              Provided password has been compromised in a data breach and cannot be used.
                            </p>
                          </if>
                          <else>
                            <p no-update class="help mb-2 mt-0">
                              Use at least ${MIN_PASSWORD_LENGTH} characters. Mix of letters, numbers and symbols.
                            </p>
                          </else>
                          <div class=["control has-icons-right", isPasswordPwned && "is-danger"]>
                            <if(isRequired)>
                              <input required=true aria-required="true" class:no-update="input" id:scoped=fieldId type=(passwordFieldVisibility["password"] ? "text" : "password") name="password" minlength=MIN_PASSWORD_LENGTH maxlength=MAX_PASSWORD_LENGTH autocomplete="new-password" />
                            </if>
                            <else>
                              <input aria-required="false" class:no-update="input" id:scoped=fieldId type=(passwordFieldVisibility["password"] ? "text" : "password") name="password" minlength=MIN_PASSWORD_LENGTH maxlength=MAX_PASSWORD_LENGTH autocomplete="new-password" />
                            </else>
                            <if(passwordFieldVisibility['password'])>
                              <span no-update data-action="toggle" data-target="password" class="icon is-small is-right has-cursor-pointer is-pointer-events-all" aria-label="Toggle password visibility: click to hide" role="button">
                                <i aria-hidden="true" class="fa-light fa-eye is-pointer-events-none"></i>
                              </span>
                            </if>
                            <else>
                              <span no-update data-action="toggle" data-target="password" class="icon is-small is-right has-cursor-pointer is-pointer-events-all" aria-label="Toggle password visibility: click to show" role="button">
                                <i aria-hidden="true" class="fa-light fa-eye-slash is-pointer-events-none"></i>
                              </span>
                            </else>
                          </div>
                          <div class:no-update="is-flex has-gap-3 is-size-7 mt-2">
                          $ {
                            const {
                              hasLowercase,
                              hasMinCharacters,
                              hasNumber,
                              hasSymbol,
                              hasUppercase,
                            } = passwordState;
                          }
                            <p no-update-body class=["is-flex is-flex-direction-column is-align-items-center is-justify-flex-start", hasMinCharacters ? "has-text-weight-bold has-text-grey-dark animate__animated animate__tada" : "has-text-grey-light"]>
                              <span aria-label=`${MIN_PASSWORD_LENGTH} plus`>${MIN_PASSWORD_LENGTH}<i class="fa-regular fa-plus"></i></span>
                              <span>Characters</span>
                            </p>
                            <p no-update-body class=["is-flex is-flex-direction-column is-align-items-center is-justify-flex-start", hasLowercase ? "has-text-weight-bold has-text-grey-dark animate__animated animate__tada" : "has-text-grey-light"]>
                              <span>a</span>
                              <span>Lowercase</span>
                            </p>
                            <p no-update-body class=["is-flex is-flex-direction-column is-align-items-center is-justify-flex-start", hasUppercase ? "has-text-weight-bold has-text-grey-dark animate__animated animate__tada" : "has-text-grey-light"]>
                              <span>A</span>
                              <span>Uppercase</span>
                            </p>
                            <p no-update-body class=["is-flex is-flex-direction-column is-align-items-center is-justify-flex-start", hasNumber ? "has-text-weight-bold has-text-grey-dark animate__animated animate__tada" : "has-text-grey-light"]>
                              <span>0</span>
                              <span>Numbers</span>
                            </p>
                            <p no-update-body class=["is-flex is-flex-direction-column is-align-items-center is-justify-flex-start", hasSymbol ? "has-text-weight-bold has-text-grey-dark animate__animated animate__tada" : "has-text-grey-light"]>
                              <span>#</span>
                              <span>Symbols</span>
                            </p>
                          </div>
                        </div>

                        $ fieldId = createId();
                        <div class:no-update="field">
                          <label no-update-body class=["label", isRequired && "required"] for:scoped=fieldId>Confirm password</label>
                          <div class:no-update="control">
                            <input required=(isRequired && true) aria-required=(isRequired ? "true" : "false") class:no-update="input" id:scoped=fieldId type=(passwordFieldVisibility["password"] ? "text" : "password") name="password-confirm" minlength=MIN_PASSWORD_LENGTH maxlength=MAX_PASSWORD_LENGTH autocomplete="new-password"/>
                          </div>
                        </div>

                        <button no-update-body name="signup" type="submit" form:scoped=formId class=["button is-dark", isLoading && "is-loading"]>
                          <span class="icon-text is-pointer-events-none">
                            <span class="icon" aria-hidden="true">
                              <i class="fa-regular fa-key"></i>
                            </span>
                            <span>
                              Sign up with Password
                            </span>
                          </span>
                        </button>
                      </div>
                    </fieldset>
                  </div>
                </label>
              </div>
            </if>

          </div>
        </div>

      </form>

    </app-box>
  </div>
</div>
